import React from 'react';
import I18n from 'i18n-js';
import { renderToString } from 'react-dom/server';
import { flatten } from 'lodash';

import { RCAlertBox, RCCheckbox } from '@reverbdotcom/cadence/components';
import { core_apimessages_BulkDeleteListingsResponse_ListingDeleted, core_apimessages_BulkPublishListingsResponse_ListingPublished, core_apimessages_Error } from '@reverbdotcom/commons/src/rql';

import { ActionType } from './sellerListingsCollectionReducer';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import { isValidForBulkSelect } from './toolbarHelpers';
import BulkPublishButton from './bulk_actions/BulkPublishButton';
import BulkDeleteButton from './bulk_actions/BulkDeleteButton';
import { SanitizedRender } from '@reverbdotcom/commons';
import PublishAllDraftsButton from './bulk_actions/PublishAllDraftsButton';
import ExportListingsButton from './bulk_actions/ExportListingsButton';
import BulkActionErrors from './bulk_actions/BulkActionErrors';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useIsMobileWindowWidth } from '@reverbdotcom/cadence/components/helpers/is-mobile';

export const COMPONENT_NAME = 'SellerListingCardsToolbar';
interface IBulkPublishCallbackArgs {
  message: string;
  listingIds?: string[],
}
interface IBaseBulkActionArgs {
  onCallback: ({ listingIds, message }: IBulkPublishCallbackArgs) => void;
}
interface IBulkSuccessArgs extends IBaseBulkActionArgs {
  listingResponses: (core_apimessages_BulkPublishListingsResponse_ListingPublished | core_apimessages_BulkDeleteListingsResponse_ListingDeleted)[];
  I18nKey: string;
}

export function handleBulkSuccesses({
  listingResponses,
  onCallback,
  I18nKey,
}: IBulkSuccessArgs) {
  const successResponses = listingResponses.filter(listing => listing.success);
  const listingIds = successResponses.map(listing => listing.listingId);
  const successCount = listingIds.length;

  if (successCount) {
    const message = I18n.t(I18nKey, {
      count: successCount,
    });

    onCallback({
      listingIds,
      message,
    });
  }
}

interface IBulkErrorArgs extends IBaseBulkActionArgs {
  errors: core_apimessages_Error[];
  totalListingsWithErrors: number;
  I18nTitleKey: string;
}

export function handleBulkErrors({
  totalListingsWithErrors,
  errors,
  onCallback,
  I18nTitleKey,
}: IBulkErrorArgs) {
  const message = renderToString(
    <BulkActionErrors
      totalListingsWithErrors={totalListingsWithErrors}
      errors={errors}
      titleI18nKey={I18nTitleKey}
    />,
  );

  if (message.length) onCallback({
    message,
  });
}

export default function SellerListingCardsToolbar() {
  const isMobile = useIsMobileWindowWidth();
  const [successMessage, setSuccessMessage] = React.useState<string>('');
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const hasSuccessMessage = !!successMessage.length;
  const hasErrorMessages = !!errorMessage.length;
  const shouldRenderAlerts = hasSuccessMessage || hasErrorMessages;

  const {
    listingsCollectionState,
    dispatch,
    useLazySellerListingsQuery,
  } = React.useContext(SellerListingsCollectionContext);

  const { refetchSellerListings } = useLazySellerListingsQuery();
  const { listings, selectedListings } = listingsCollectionState;

  const hasListings = !!listings.length;
  const isAllSelected = hasListings && listings.length == selectedListings.length;
  const isSomeSelected = !!selectedListings.length && !isAllSelected;

  const I18nSelectKey = (isAllSelected || isSomeSelected) ? 'selectedListings' : 'selectListings';


  const isValidForToolbar = isValidForBulkSelect(window);

  function showSelectionButtons() {
    if (!isMobile) return true;

    return isAllSelected || isSomeSelected;
  }

  function updateSelections() {
    if (listings.length == selectedListings.length) {
      dispatch({
        type: ActionType.DESELECT_ALL_LISTINGS,
      });
    } else {
      dispatch({
        type: ActionType.SELECT_ALL_LISTINGS,
      });
    }
  }

  function checkedState() {
    if (isAllSelected) return true;
    if (isSomeSelected) return 'mixed';

    return false;
  }

  function resetSuccessAndErrorMessages() {
    setSuccessMessage('');
    setErrorMessage('');
  }

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
  }, isValidForToolbar);

  if (!isValidForToolbar) return null;

  return (
    <div className="seller-listing-cards-toolbar__toolbar">
      {shouldRenderAlerts && (
        <div
          className="seller-listing-cards-toolbar__toolbar__alerts"
        >
          {hasSuccessMessage && (
            <RCAlertBox
              type="success"
              onDismiss={() => setSuccessMessage('')}
            >
              <SanitizedRender
                html={successMessage}
              />
            </RCAlertBox>
          )}

          {hasErrorMessages && (
            <RCAlertBox
              type="error"
              onDismiss={() => setErrorMessage('')}
            >
              <SanitizedRender
                html={errorMessage}
              />
            </RCAlertBox>
          )}
        </div>
      )}

      <div className="seller-listing-cards-toolbar__toolbar__actions">
        <div className="ws-nowrap ml-4">
          <RCCheckbox
            disabled={!hasListings}
            checked={checkedState()}
            triState={isSomeSelected}
            label={
              <div className="weight-bold">
                {I18n.t(`discovery.listingsManagement.toolbar.${I18nSelectKey}`, {
                  count: selectedListings.length,
                })}
              </div>
            }
            onClick={() => updateSelections()}
            onChange={() => updateSelections()}
          />
        </div>

        <div className="d-flex fx-justify-between tablet-gap-3 fx-align-center tablet-fx-dir-col-reverse width-100">
          {showSelectionButtons() && (
            <div className="d-flex gap-3 fx-wrap tablet-width-100">
              <div className="tablet-width-100">
                <BulkDeleteButton
                  selectedListings={selectedListings}
                  onErrorCallback={message => setErrorMessage(message)}
                  onSuccessCallback={listingResponses => {
                    resetSuccessAndErrorMessages();

                    handleBulkSuccesses({
                      listingResponses,
                      I18nKey: 'discovery.listingsManagement.toolbar.bulkActions.bulkDelete.deleteSuccessful',
                      onCallback: ({ listingIds, message }) => {
                        setSuccessMessage(message);
                        dispatch({
                          type: ActionType.REMOVE_LISTINGS,
                          payload: {
                            listingIds: listingIds,
                          },
                        });
                      },
                    });

                    const errorListingResponses = listingResponses.filter(response => !response.success);
                    const errors = flatten(
                      errorListingResponses.map(response => response.deleteErrors),
                    );

                    handleBulkErrors({
                      totalListingsWithErrors: errorListingResponses.length,
                      errors,
                      I18nTitleKey: 'discovery.listingsManagement.toolbar.bulkActions.bulkDelete.deleteErrors.title',
                      onCallback: ({ message }) => {
                        setErrorMessage(message);
                      },
                    });
                  }}
                />
              </div>

              <div className="tablet-width-100">
                <BulkPublishButton
                  selectedListings={selectedListings}
                  onErrorCallback={message => setErrorMessage(message)}
                  onSuccessCallback={listingResponses => {
                    resetSuccessAndErrorMessages();
                    handleBulkSuccesses({
                      listingResponses,
                      I18nKey: 'discovery.listingsManagement.toolbar.bulkActions.bulkPublish.publishSuccessful',
                      onCallback: ({ listingIds, message }) => {
                        setSuccessMessage(message);
                        refetchSellerListings(listingIds);
                      },
                    });

                    const errorListingResponses = listingResponses.filter(response => !response.success);
                    const errors = flatten(
                      errorListingResponses.map(response => response.publishErrors),
                    );

                    handleBulkErrors({
                      totalListingsWithErrors: errorListingResponses.length,
                      errors,
                      I18nTitleKey: 'discovery.listingsManagement.toolbar.bulkActions.bulkPublish.publishErrors.title',
                      onCallback: ({ message }) => {
                        setErrorMessage(message);
                      },
                    });
                  }}
                />
              </div>
            </div>
          )}

          <div className="d-flex gap-3 fx-wrap tablet-width-100">
            <div className="tablet-width-100">
              <PublishAllDraftsButton
                onSuccessCallback={message => {
                  resetSuccessAndErrorMessages();
                  setSuccessMessage(message);
                }}
                onErrorCallback={message => {
                  resetSuccessAndErrorMessages();
                  setErrorMessage(message);
                }}
              />
            </div>

            <div className="tablet-width-100">
              <div className="tablet-d-none">
                <ExportListingsButton
                  selectedListings={selectedListings}
                  onSuccessCallback={message => {
                    resetSuccessAndErrorMessages();
                    setSuccessMessage(message);
                  }}
                  onErrorCallback={message => {
                    resetSuccessAndErrorMessages();
                    setErrorMessage(message);
                  }}
                />
              </div>

              <div className="tablet-d-block d-none">
                <ExportListingsButton
                  selectedListings={selectedListings}
                  onSuccessCallback={message =>{
                    resetSuccessAndErrorMessages();
                    setSuccessMessage(message);
                  }}
                  onErrorCallback={message => {
                    resetSuccessAndErrorMessages();
                    setErrorMessage(message);
                  }}
                  fullSizeButton
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
